import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { useSelectedTeam } from '../../lib/teamConfig';
import { getBasketballTeams, getBasketballPlayers } from '../../utils/basketballDataService';
import { fetchNatStatPlayers } from '../../utils/natstatDataService';
import { useUpcomingOpponent } from '../../hooks/useUpcomingOpponent';
import { Users, Search, Filter, ChevronRight, Star, Activity, Zap, Award, Calendar, Loader2, Heart } from 'lucide-react';
import { generateBiometricProfile } from '../../utils/biometricDataGenerator';
import OpponentPlayerDrawer from '../../components/basketball/OpponentPlayerDrawer';

/**
 * BasketballRosterManagement - Roster management for basketball teams
 * 2025-26 Season - Updated with Real Madrid player photos and OLED design
 *
 * Features:
 * - Team selection (NBA, WNBA, College, EuroLeague)
 * - Player roster display with photos
 * - Search and filter capabilities
 * - OLED black responsive design
 */
export default function BasketballRosterManagement() {
  const navigate = useNavigate();
  const context = useOutletContext();

  // Get selected team from TeamSelector context
  const { selectedTeam: contextTeam } = useSelectedTeam();

  // State - use uppercase sport codes to match basketballDataService
  // Initialize from context if available (from TeamSelector onboarding)
  const [selectedLeague, setSelectedLeague] = useState(() => {
    // Map league codes from TeamSelector to basketballDataService format
    const leagueCode = contextTeam?.league?.code;
    if (leagueCode) {
      // Map NatStat codes to basketballDataService codes
      const leagueMap = {
        'NBA': 'NBA',
        'MBB': 'MBB',  // Men's College Basketball
        'WBB': 'WBB',  // Women's College Basketball
        'WNBA': 'WNBA',
        'EURO': 'EURO',
        'GLEAGUE': 'GLEAGUE',
      };
      return leagueMap[leagueCode] || leagueCode;
    }
    return 'NBA'; // Default to NBA
  });
  const [selectedTeam, setSelectedTeam] = useState(() => {
    // Use team name from context if available
    return contextTeam?.team?.team_name || contextTeam?.team?.name || null;
  });
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Opponent player drawer state
  const [drawerPlayer, setDrawerPlayer] = useState(null);

  // Track if we're using NatStat data (from TeamSelector) or legacy data
  const useNatStat = !!contextTeam?.league?.code;

  // Get upcoming opponent data
  const {
    opponent,
    opponentRoster,
    nextGame,
    loading: opponentLoading,
    hasOpponent,
  } = useUpcomingOpponent();

  // Load teams when league changes (only if not pre-selected from context)
  useEffect(() => {
    // If we have a pre-selected team from context, skip team loading
    if (contextTeam?.team && selectedTeam) {
      // Just load players directly
      return;
    }

    setTeamsLoading(true);
    setPlayers([]);

    getBasketballTeams(selectedLeague)
      .then((data) => {
        setTeams(data || []);
        // If no team selected, auto-select first team
        if (!selectedTeam && data?.length > 0) {
          setSelectedTeam(data[0].name);
        }
      })
      .catch((err) => {
        console.error('Error loading teams:', err);
        setTeams([]);
      })
      .finally(() => {
        setTeamsLoading(false);
      });
  }, [selectedLeague, contextTeam, selectedTeam]);

  // Load players when team is selected
  useEffect(() => {
    if (!selectedTeam) return;

    setLoading(true);

    // Try NatStat first if we have context from TeamSelector
    if (useNatStat && contextTeam?.league?.code && contextTeam?.team?.team_code) {
      fetchNatStatPlayers(contextTeam.league.code, { teamId: contextTeam.team.team_code, limit: 100 })
        .then((data) => {
          if (data && data.length > 0) {
            // Map NatStat player format to display format
            const mappedPlayers = data.map(p => ({
              // CRITICAL: Include 'id' field for navigation URL
              id: p.player_id || p.id,
              playerId: p.player_id,
              name: p.player_name || p.name,
              position: p.position,
              number: p.jersey_number || p.number,
              height: p.height,
              weight: p.weight,
              birthdate: p.birthdate,
              photo: p.photo_url || null,
              image: p.photo_url || null,
            }));
            setPlayers(mappedPlayers);
          } else {
            // Fallback to legacy data service
            return getBasketballPlayers(selectedLeague, selectedTeam).then(setPlayers);
          }
        })
        .catch((err) => {
          console.error('NatStat players error, falling back:', err);
          // Fallback to legacy
          getBasketballPlayers(selectedLeague, selectedTeam)
            .then((data) => setPlayers(data || []))
            .catch(() => setPlayers([]));
        })
        .finally(() => setLoading(false));
    } else {
      // Use legacy basketball data service
      getBasketballPlayers(selectedLeague, selectedTeam)
        .then((data) => {
          setPlayers(data || []);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [selectedLeague, selectedTeam, useNatStat, contextTeam]);

  // Filter players by search
  const filteredPlayers = useMemo(() => {
    if (!searchQuery) return players;
    const query = searchQuery.toLowerCase();
    return players.filter(
      (player) =>
        player.name?.toLowerCase().includes(query) ||
        player.position?.toLowerCase().includes(query) ||
        player.number?.toString().includes(query)
    );
  }, [players, searchQuery]);

  // Position color mapping for basketball
  const positionColors = {
    PG: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
    SG: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
    SF: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
    PF: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
    C: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
    G: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
    F: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
  };

  const getPositionColor = (position) => {
    const pos = position?.split('-')[0]?.trim().toUpperCase();
    return positionColors[pos] || { bg: 'bg-white/10', text: 'text-white/60', border: 'border-white/20' };
  };

  // League is determined from TeamSelector context - no manual selection needed

  // Player card component for grid view with biometric wearable data
  const PlayerCard = ({ player }) => {
    const posColor = getPositionColor(player.position);
    const hasImage = player.image && !player.image.includes('undefined');
    const playerId = player.id || player.playerId || player.player_id;

    // Generate biometric profile for this player
    const biometrics = useMemo(
      () => generateBiometricProfile(playerId, player.name),
      [playerId, player.name]
    );

    return (
      <button
        onClick={() => navigate(`/basketball/player/${playerId}?team=${encodeURIComponent(selectedTeam)}&league=${selectedLeague}`)}
        className="group relative bg-black border border-[#1A1A1A] rounded-[14px] overflow-hidden transition-all duration-300 hover:border-[#E8B800]/50 hover:shadow-lg hover:shadow-[#E8B800]/10"
      >
        {/* Player Image */}
        <div className="aspect-[3/4] relative overflow-hidden bg-gradient-to-b from-white/5 to-black">
          {hasImage ? (
            <img
              src={player.image}
              alt={player.name}
              className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          {/* Fallback avatar */}
          <div
            className={`w-full h-full ${hasImage ? 'hidden' : 'flex'} items-center justify-center bg-gradient-to-b from-white/10 to-black`}
          >
            <span className="text-5xl font-bold text-white/20">#{player.number}</span>
          </div>

          {/* Jersey number badge */}
          <div className="absolute top-2 left-2 px-2 py-1 bg-black/80 backdrop-blur-sm rounded text-[#F5F5F5] font-bold text-lg">
            #{player.number}
          </div>

          {/* Position badge */}
          <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold ${posColor.bg} ${posColor.text} border ${posColor.border}`}>
            {player.position}
          </div>

          {/* Biometric readiness indicator - top right corner below position */}
          {biometrics.hasAnyDevice && biometrics.compositeReadiness && (
            <div
              className="absolute top-10 right-2 px-2 py-1 rounded bg-black/70 backdrop-blur-sm flex items-center gap-1"
              title={`Readiness: ${biometrics.compositeReadiness}%`}
            >
              <Heart size={10} className="text-red-400" />
              <span className={`text-xs font-bold ${
                biometrics.compositeReadiness >= 85 ? 'text-emerald-400' :
                biometrics.compositeReadiness >= 70 ? 'text-green-400' :
                biometrics.compositeReadiness >= 55 ? 'text-amber-400' : 'text-red-400'
              }`}>
                {biometrics.compositeReadiness}%
              </span>
            </div>
          )}

          {/* Gradient overlay */}
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black to-transparent" />
        </div>

        {/* Player Info */}
        <div className="p-4">
          <h3 className="text-[#F5F5F5] font-semibold text-lg truncate">{player.name}</h3>
          <p className="text-[#999999] text-sm">{player.height} • {player.weight} lbs</p>

          {/* Stats Row */}
          {player.stats && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              <div className="text-center">
                <div className="text-[#E8B800] font-bold">{player.stats.ppg || '--'}</div>
                <div className="text-[#555555] text-xs">PPG</div>
              </div>
              <div className="text-center">
                <div className="text-[#F5F5F5] font-bold">{player.stats.rpg || '--'}</div>
                <div className="text-[#555555] text-xs">RPG</div>
              </div>
              <div className="text-center">
                <div className="text-[#F5F5F5] font-bold">{player.stats.apg || '--'}</div>
                <div className="text-[#555555] text-xs">APG</div>
              </div>
            </div>
          )}

          {/* PIR for EuroLeague */}
          {selectedLeague === 'EURO' && player.stats?.pir && (
            <div className="mt-2 flex items-center justify-center gap-1 py-1 px-2 bg-[#E8B800]/10 rounded">
              <Award size={12} className="text-[#E8B800]" />
              <span className="text-[#E8B800] text-xs font-medium">{player.stats.pir} PIR</span>
            </div>
          )}

          {/* Biometric Wearables Section */}
          <div className="mt-3 pt-3 border-t border-[#1A1A1A]">
            {biometrics.hasAnyDevice ? (
              <div className="space-y-2">
                {/* Connected devices row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    {Object.entries(biometrics.devices)
                      .filter(([_, data]) => data.connected)
                      .slice(0, 4)
                      .map(([device, data]) => (
                        <div
                          key={device}
                          className="w-5 h-5 rounded-md flex items-center justify-center"
                          style={{ backgroundColor: `${data.color}20` }}
                          title={data.deviceName}
                        >
                          {device === 'whoop' && <Zap size={10} style={{ color: data.color }} />}
                          {device === 'oura' && <span style={{ color: data.color, fontSize: '8px' }}>O</span>}
                          {device === 'neuphoria' && <span style={{ color: data.color, fontSize: '8px' }}>N</span>}
                          {device === 'garmin' && <Activity size={10} style={{ color: data.color }} />}
                          {device === 'appleWatch' && <span style={{ color: data.color, fontSize: '8px' }}>A</span>}
                        </div>
                      ))}
                  </div>
                  <span className="text-[10px] text-[#555555]">{biometrics.lastSync}</span>
                </div>

                {/* Key metrics row */}
                <div className="grid grid-cols-3 gap-1">
                  {biometrics.devices.whoop?.connected && (
                    <div className="text-center px-1 py-1 rounded bg-white/[0.03]">
                      <div className="text-xs font-semibold" style={{ color: biometrics.devices.whoop.color }}>
                        {biometrics.devices.whoop.strain}
                      </div>
                      <div className="text-[8px] text-[#555555]">STRAIN</div>
                    </div>
                  )}
                  {biometrics.devices.oura?.connected && (
                    <div className="text-center px-1 py-1 rounded bg-white/[0.03]">
                      <div className="text-xs font-semibold" style={{ color: biometrics.devices.oura.color }}>
                        {biometrics.devices.oura.sleepScore}
                      </div>
                      <div className="text-[8px] text-[#555555]">SLEEP</div>
                    </div>
                  )}
                  {biometrics.devices.neuphoria?.connected && (
                    <div className="text-center px-1 py-1 rounded bg-white/[0.03]">
                      <div className="text-xs font-semibold" style={{ color: biometrics.devices.neuphoria.color }}>
                        {biometrics.devices.neuphoria.focusScore}
                      </div>
                      <div className="text-[8px] text-[#555555]">FOCUS</div>
                    </div>
                  )}
                  {biometrics.devices.garmin?.connected && !biometrics.devices.neuphoria?.connected && (
                    <div className="text-center px-1 py-1 rounded bg-white/[0.03]">
                      <div className="text-xs font-semibold" style={{ color: biometrics.devices.garmin.color }}>
                        {biometrics.devices.garmin.vo2Max}
                      </div>
                      <div className="text-[8px] text-[#555555]">VO2</div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 py-1">
                <Heart size={12} className="text-white/20" />
                <span className="text-[10px] text-[#555555]">No wearables</span>
              </div>
            )}
          </div>

          {/* Role badge */}
          {player.role && (
            <p className="mt-2 text-[#999999] text-xs truncate" title={player.role}>
              {player.role}
            </p>
          )}
        </div>

        {/* Hover indicator */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#E8B800] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
      </button>
    );
  };

  return (
    <div className="min-h-full bg-black text-[#F5F5F5] p-6 space-y-6 pb-20">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#F5F5F5]">
            Roster Management
          </h1>
          <p className="mt-1 text-[#999999]">
            View and manage your basketball team roster • 2025-26 Season
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View mode toggle */}
          <button
            onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            className={`p-2 rounded-lg transition-colors ${
              viewMode === 'grid' ? 'bg-[#E8B800] text-black' : 'bg-white/10 text-white/60 hover:bg-white/20'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {viewMode === 'grid' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              )}
            </svg>
          </button>
          <Users size={32} className="text-[#E8B800]" />
        </div>
      </div>

      {/* Team header showing selected team from onboarding context */}
      {contextTeam && (
        <div className="flex items-center justify-between p-4 rounded-[14px] border border-[#1A1A1A] bg-[#0A0A0A]">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-[14px] bg-[#E8B800]/20 flex items-center justify-center text-2xl font-bold text-[#E8B800]">
              {contextTeam.team?.team_name?.charAt(0) || '?'}
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#F5F5F5]">{contextTeam.team?.team_name}</h2>
              <p className="text-sm text-[#999999]">
                {contextTeam.league?.name} • {contextTeam.sport?.charAt(0).toUpperCase() + contextTeam.sport?.slice(1)}
              </p>
            </div>
          </div>
          <button
            onClick={() => navigate('/select-sport')}
            className="px-4 py-2 rounded-lg text-sm font-medium text-[#E8B800] hover:bg-[#E8B800]/10 transition-colors"
          >
            Change Team
          </button>
        </div>
      )}

      {/* Upcoming Opponent Section */}
      {contextTeam && (
        <div className="p-4 rounded-[14px] border border-red-500/20 bg-red-500/5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[#F5F5F5]">Upcoming Opponent</h3>
                {nextGame && (
                  <p className="text-sm text-[#999999]">
                    {(() => {
                      const raw = nextGame.game_date;
                      const d = raw && typeof raw === 'object' && raw.value
                        ? new Date(raw.value + 'T00:00:00')
                        : new Date(raw);
                      return !isNaN(d?.getTime())
                        ? d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                        : 'TBD';
                    })()}
                    {opponent?.isHome ? ' • Home' : ' • Away'}
                    {nextGame.venue && <span className="ml-1">• {nextGame.venue}</span>}
                  </p>
                )}
              </div>
            </div>
            {opponent && (
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  opponent.isHome
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-orange-500/20 text-orange-400'
                }`}>
                  {opponent.isHome ? 'HOME' : 'AWAY'}
                </span>
              </div>
            )}
          </div>

          {opponentLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-red-400 animate-spin" />
              <span className="ml-3 text-[#999999]">Loading opponent data...</span>
            </div>
          ) : hasOpponent ? (
            <>
              {/* Opponent Team Header */}
              <div className="flex items-center gap-4 mb-4 p-3 rounded-lg bg-[#0A0A0A]">
                <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center text-xl font-bold text-red-400 overflow-hidden">
                  {opponent.team_logo ? (
                    <img src={opponent.team_logo} alt={opponent.team_name} className="w-10 h-10 object-contain" />
                  ) : (
                    opponent.team_name?.charAt(0) || '?'
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-[#F5F5F5]">{opponent.team_name}</h4>
                  <p className="text-sm text-[#999999]">
                    {opponentRoster.length} players on roster
                  </p>
                </div>
                {nextGame?.status && nextGame.status.toLowerCase() !== 'scheduled' && (
                  <div className="text-right">
                    <div className="text-2xl font-bold text-[#F5F5F5]">
                      {nextGame.home_score} - {nextGame.visitor_score}
                    </div>
                    <div className="text-xs text-[#999999] uppercase">{nextGame.status}</div>
                  </div>
                )}
              </div>

              {/* Opponent Roster Preview */}
              {opponentRoster.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {opponentRoster.slice(0, 12).map((player) => (
                    <button
                      key={player.player_id}
                      onClick={() => setDrawerPlayer(player)}
                      className="group p-3 rounded-lg bg-[#0A0A0A] border border-[#1A1A1A] hover:border-red-400/30 transition-all text-left"
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center text-sm font-bold text-white/60">
                          {player.jersey_number || player.player_name?.charAt(0) || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-[#F5F5F5] truncate">
                            {player.player_name}
                          </div>
                          <div className="text-xs text-[#999999]">
                            {player.position}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-[#999999] text-sm">
                  No roster data available for opponent
                </div>
              )}
              {opponentRoster.length > 12 && (
                <div className="text-center mt-3">
                  <span className="text-xs text-[#999999]">
                    +{opponentRoster.length - 12} more players
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-white/20" />
              <p className="text-[#999999]">No upcoming games found</p>
              <p className="text-sm text-[#555555] mt-1">
                Schedule data will appear when available
              </p>
            </div>
          )}
        </div>
      )}

      {/* Team Selection - Only show if no team from context */}
      {!contextTeam && (
      <div className="p-4 rounded-[14px] border border-[#1A1A1A] bg-black">
        <h2 className="text-lg font-semibold mb-3 text-[#F5F5F5]">
          Select Team
        </h2>
        {teamsLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-[#E8B800] border-t-transparent rounded-full mx-auto" />
            <p className="mt-3 text-[#999999]">Loading teams...</p>
          </div>
        ) : teams.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {teams.map((team) => (
              <button
                key={team.name || team.id}
                onClick={() => setSelectedTeam(team.name)}
                className={`p-3 rounded-lg text-left transition-all ${
                  selectedTeam === team.name
                    ? 'bg-[#E8B800] text-black'
                    : 'bg-[#0A0A0A] text-white/70 hover:bg-[rgba(255,255,255,0.04)] hover:text-[#F5F5F5] border border-[#1A1A1A]'
                }`}
              >
                <div className="font-medium text-sm truncate">{team.name}</div>
                {team.city && (
                  <div className={`text-xs ${selectedTeam === team.name ? 'text-black/60' : 'text-[#555555]'}`}>
                    {team.city}
                  </div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <Users size={48} className="mx-auto mb-3 text-white/20" />
            <p className="text-[#999999]">
              No teams available for this league
            </p>
          </div>
        )}
      </div>
      )}

      {/* Player Search */}
      {selectedTeam && (
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search
              size={18}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#555555]"
            />
            <input
              type="text"
              placeholder="Search players by name, position, or number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border bg-black border-[#1A1A1A] text-[#F5F5F5] placeholder-white/40 focus:outline-none focus:border-[#E8B800]/50"
            />
          </div>
          <button
            className="p-2.5 rounded-lg bg-[#0A0A0A] text-white/60 border border-[#1A1A1A] hover:bg-[rgba(255,255,255,0.04)]"
          >
            <Filter size={20} />
          </button>
        </div>
      )}

      {/* Player Roster */}
      {selectedTeam && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-[#F5F5F5]">
              {selectedTeam} Roster
            </h2>
            <p className="text-[#999999] text-sm">
              {filteredPlayers.length} players
            </p>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-[#E8B800] border-t-transparent rounded-full mx-auto" />
              <p className="mt-3 text-[#999999]">Loading roster...</p>
            </div>
          ) : filteredPlayers.length > 0 ? (
            viewMode === 'grid' ? (
              // Grid View
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {filteredPlayers.map((player, index) => (
                  <PlayerCard key={player.id || index} player={player} />
                ))}
              </div>
            ) : (
              // List View with Biometrics
              <div className="rounded-[14px] border border-[#1A1A1A] overflow-hidden">
                <div className="divide-y divide-[#1A1A1A]">
                  {filteredPlayers.map((player, index) => {
                    const posColor = getPositionColor(player.position);
                    const hasImage = player.image && !player.image.includes('undefined');
                    const playerId = player.id || player.playerId || player.player_id || index;
                    const biometrics = generateBiometricProfile(playerId, player.name);

                    return (
                      <button
                        key={playerId}
                        onClick={() => navigate(`/basketball/player/${playerId}?team=${encodeURIComponent(selectedTeam)}&league=${selectedLeague}`)}
                        className="w-full p-4 flex items-center gap-4 text-left transition-colors hover:bg-[rgba(255,255,255,0.04)]"
                      >
                        {/* Player Avatar/Photo */}
                        <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#111111] flex-shrink-0 relative">
                          {hasImage ? (
                            <img
                              src={player.image}
                              alt={player.name}
                              className="w-full h-full object-cover object-top"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className={`w-full h-full ${hasImage ? 'hidden' : 'flex'} items-center justify-center text-[#E8B800] font-bold`}
                          >
                            #{player.number}
                          </div>
                          {/* Biometric status dot */}
                          {biometrics.hasAnyDevice && (
                            <div
                              className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-black ${
                                biometrics.compositeReadiness >= 85 ? 'bg-emerald-500' :
                                biometrics.compositeReadiness >= 70 ? 'bg-green-500' :
                                biometrics.compositeReadiness >= 55 ? 'bg-amber-500' : 'bg-red-500'
                              }`}
                              title={`Readiness: ${biometrics.compositeReadiness}%`}
                            />
                          )}
                        </div>

                        {/* Player Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-[#F5F5F5]">
                              {player.name}
                            </span>
                            {player.isStarter && (
                              <Star size={14} className="text-[#E8B800] fill-[#E8B800]" />
                            )}
                          </div>
                          <div className="text-sm text-[#999999]">
                            {player.height} • {player.weight} lbs
                            {player.role && <span className="ml-2">• {player.role}</span>}
                          </div>
                        </div>

                        {/* Biometrics Column - Wearable devices */}
                        <div className="hidden lg:flex items-center gap-2">
                          {Object.entries(biometrics.devices)
                            .filter(([_, data]) => data.connected)
                            .slice(0, 3)
                            .map(([device, data]) => (
                              <div
                                key={device}
                                className="w-2.5 h-2.5 rounded-full"
                                style={{ backgroundColor: data.color }}
                                title={`${data.deviceName}: Connected`}
                              />
                            ))}
                          {biometrics.compositeReadiness && (
                            <span className={`text-sm font-semibold ${
                              biometrics.compositeReadiness >= 85 ? 'text-emerald-400' :
                              biometrics.compositeReadiness >= 70 ? 'text-green-400' :
                              biometrics.compositeReadiness >= 55 ? 'text-amber-400' : 'text-red-400'
                            }`}>
                              {biometrics.compositeReadiness}%
                            </span>
                          )}
                        </div>

                        {/* Position Badge */}
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${posColor.bg} ${posColor.text}`}>
                          {player.position}
                        </div>

                        {/* Stats Preview */}
                        {player.stats && (
                          <div className="text-right text-sm text-[#999999] hidden md:block">
                            <div className="text-[#E8B800] font-semibold">{player.stats.ppg || '--'} PPG</div>
                            <div className="text-xs">
                              {selectedLeague === 'EURO' && player.stats.pir
                                ? `${player.stats.pir} PIR`
                                : `${player.stats.apg || '--'} APG / ${player.stats.rpg || '--'} RPG`
                              }
                            </div>
                          </div>
                        )}

                        <ChevronRight size={20} className="text-[#555555]" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )
          ) : (
            <div className="p-8 text-center rounded-[14px] border border-[#1A1A1A]">
              <Users size={48} className="mx-auto mb-3 text-white/20" />
              <p className="text-[#999999]">
                {searchQuery ? 'No players match your search' : 'No players found for this team'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* No Team Selected State */}
      {!selectedTeam && !teamsLoading && (
        <div className="p-8 rounded-[14px] border border-[#1A1A1A] text-center">
          <Users size={48} className="mx-auto mb-3 text-[#E8B800]/50" />
          <p className="text-[#999999]">
            Select a team above to view their roster
          </p>
        </div>
      )}

      {/* Opponent Player Drawer */}
      {drawerPlayer && (
        <OpponentPlayerDrawer
          player={drawerPlayer}
          league={contextTeam?.league?.code || selectedLeague}
          teamCode={opponent?.team_code}
          teamName={opponent?.team_name}
          onClose={() => setDrawerPlayer(null)}
        />
      )}
    </div>
  );
}
